import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConfirmModal from '@/domains/dashboard-management/components/ConfirmModal/ConfirmModal';
import BottomAction from '@/domains/mobile-web/components/BottomAction/BottomAction';
import CardDetailAddress from '@/domains/mobile-web/components/CardDetailAddress/CardDetailAddress';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useAddresses, useDeleteAddress } from '@/domains/mobile-web/hooks/useAddresses';
import { formatAddress } from '@/domains/mobile-web/utils/address';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import { useCheckoutStore } from '@/store/checkoutStore';

/** Opened from checkout with ?select=1: a card is chosen and "Pilih Alamat" takes it back there. */
const AccountAddress = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSelecting = searchParams.get('select') === '1';
  const { data, isLoading, isError } = useAddresses();
  const deleteAddress = useDeleteAddress();
  const checkoutAddressId = useCheckoutStore((state) => state.addressId);
  const setCheckoutAddressId = useCheckoutStore((state) => state.setAddressId);
  const [pickedId, setPickedId] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const addresses = data?.rows ?? [];

  // Picked here, else the one checkout uses, else the main address (a deleted one falls through)
  const chosenId = [pickedId, checkoutAddressId, addresses.find((row) => row.is_default)?.id].find(
    (id) => id && addresses.some((row) => row.id === id)
  );

  const handleChoose = () => {
    if (!chosenId) return;
    setCheckoutAddressId(chosenId);
    navigate(-1);
  };

  const handleConfirmDelete = () => {
    if (!deleteId) return;
    deleteAddress.mutate(deleteId, { onSuccess: () => setDeleteId(null) });
  };

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader
          type="backAction"
          title="Daftar Alamat"
          onBack={() => navigate(-1)}
          onAdd={() => navigate('/store/setting')}
        />
      </HeaderMobile>
      <WrapperMobile className="mb-[120px]">
        {isLoading ? null : isError ? (
          <p className="body1 text-text-grey">Gagal memuat daftar alamat.</p>
        ) : addresses.length === 0 ? (
          <p className="body1 text-text-grey">Belum ada alamat tersimpan.</p>
        ) : (
          <div
            className="flex flex-col gap-6"
            {...(isSelecting && { role: 'radiogroup', 'aria-label': 'Pilih alamat pengiriman' })}
          >
            {addresses.map((address) => (
              <CardDetailAddress
                key={address.id}
                user={address.recipient_name}
                location={address.label ?? ''}
                phone={address.phone_number}
                address={formatAddress(address)}
                postalCode={address.postal_code}
                isPrimary={address.is_default}
                onEdit={() => navigate(`/store/setting/${address.id}`)}
                onDelete={() => setDeleteId(address.id)}
                deleteDisabled={deleteAddress.isPending && deleteAddress.variables === address.id}
                onSelect={isSelecting ? () => setPickedId(address.id) : undefined}
                isSelected={address.id === chosenId}
              />
            ))}
          </div>
        )}
      </WrapperMobile>
      {isSelecting && (
        <BottomAction textButton="Pilih Alamat" onClick={handleChoose} disabled={!chosenId} />
      )}

      <ConfirmModal
        open={deleteId !== null}
        title="Hapus Alamat?"
        message="Apakah Anda yakin ingin hapus alamat terpilih?"
        confirmLabel="Ya, Hapus"
        variant="danger"
        loading={deleteAddress.isPending}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteId(null)}
      />
    </main>
  );
};

export default AccountAddress;
